import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

// Table container
interface TableProps {
  children: ReactNode;
  className?: string;
}

export function Table({ children, className }: TableProps) {
  return (
    <div className={cn('overflow-x-auto rounded-lg border border-border-default', className)}>
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

// Table header
interface TableHeaderProps {
  children: ReactNode;
  className?: string;
}

export function TableHeader({ children, className }: TableHeaderProps) {
  return (
    <thead className={cn('bg-bg-tertiary border-b border-border-default', className)}>
      {children}
    </thead>
  );
}

// Table body
interface TableBodyProps {
  children: ReactNode;
  className?: string;
}

export function TableBody({ children, className }: TableBodyProps) {
  return <tbody className={cn('divide-y divide-border-default', className)}>{children}</tbody>;
}

// Table row
interface TableRowProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
}

export function TableRow({ children, className, onClick, hover = true }: TableRowProps) {
  return (
    <tr
      className={cn(
        'bg-bg-secondary',
        hover && 'hover:bg-bg-tertiary transition-colors',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

// Table head cell (sortable)
export type SortDirection = 'asc' | 'desc' | null;

interface TableHeadProps {
  children: ReactNode;
  className?: string;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}

export function TableHead({
  children,
  className,
  sortable = false,
  sortDirection,
  onSort,
}: TableHeadProps) {
  const SortIcon = sortDirection === 'asc'
    ? ChevronUp
    : sortDirection === 'desc'
      ? ChevronDown
      : ChevronsUpDown;

  return (
    <th
      className={cn(
        'px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide',
        sortable && 'cursor-pointer hover:text-text-primary select-none',
        className
      )}
      onClick={sortable ? onSort : undefined}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortable && <SortIcon className="w-3.5 h-3.5" />}
      </div>
    </th>
  );
}

// Table cell
interface TableCellProps {
  children: ReactNode;
  className?: string;
  mono?: boolean;
}

export function TableCell({ children, className, mono = false }: TableCellProps) {
  return (
    <td
      className={cn(
        'px-4 py-3 text-text-primary',
        mono && 'font-mono text-xs',
        className
      )}
    >
      {children}
    </td>
  );
}

